import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { CheckmarkCircleRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { HoursApi } from "@/services/api/hours-api";
import type {
  CmeMemberHoursSource,
  DynamicFieldsRequest,
  DynamicHoursField,
  DynamicHoursRow,
  DynamicHoursSaveRow,
  DynamicHoursSection,
  DynamicHoursSource,
  RealEstateMemberHoursSource,
  TupleHoursSource,
  WeeklyHoursFormKind,
} from "./weekly-hours-types";

type DynamicKind = Extract<WeeklyHoursFormKind, "cme" | "real-estate" | "consumer-retail" | "gpc">;

interface DynamicHoursFormProps {
  kind: DynamicKind;
  currentUserId?: number;
}

function ownCmeRow(rows: CmeMemberHoursSource[], currentUserId?: number): CmeMemberHoursSource | undefined {
  if (currentUserId != null) return rows.find((row) => row.UserId === currentUserId);
  return rows.length === 1 && !rows[0].IsStaffer ? rows[0] : undefined;
}

function taskCommentRequest(taskTypeId: number, projectTypeId: number, taskIds?: number[]): DynamicFieldsRequest {
  return { objectType: "TaskComment", objectId: 0, taskTypeId, projectTypeId, taskIds };
}

function readOnlySource(title: string, subtitle: string, message: string): DynamicHoursSource {
  return { title, subtitle, readOnlyMessage: message, isRealEstate: false, excludedLabels: [], sections: [] };
}

function tupleSource(source: TupleHoursSource, title: string, subtitle: string): DynamicHoursSource {
  if (source.Item2 !== false || !source.Item3 || !source.Item4) {
    // The modern subdepartment configuration marks these dashboards isStaffer=true
    // (read-only); only the legacy setting path unlocks the editable form.
    return readOnlySource(
      title,
      subtitle,
      `${title} weekly hours are read-only for your current TRIYO dashboard configuration.`
    );
  }
  return {
    title,
    subtitle,
    readOnlyMessage: null,
    isRealEstate: false,
    excludedLabels: ["Description"],
    sections: [{ key: "member-log", heading: null, request: taskCommentRequest(source.Item4, source.Item3), rows: [] }],
  };
}

function ownRealEstateRow(
  source: { RETrackersDtos?: RealEstateMemberHoursSource[]; CurrentUserId?: number },
  currentUserId?: number
) {
  const resolvedId = source.CurrentUserId ?? currentUserId;
  if (resolvedId != null) return source.RETrackersDtos?.find((row) => row.UserId === resolvedId);
  return source.RETrackersDtos?.length === 1 ? source.RETrackersDtos[0] : undefined;
}

const CME_TITLE = "CME weekly time log";
const CME_SUBTITLE = "Update your capacity tracker and per-deal M–F, Saturday, Sunday, and workload values.";

async function loadSource(kind: DynamicKind, currentUserId?: number): Promise<DynamicHoursSource> {
  let source: DynamicHoursSource;

  if (kind === "cme") {
    const members = await HoursApi.getCmeMemberHoursSource();
    if (!Array.isArray(members)) throw new Error("TRIYO did not return the CME member time log.");
    const member = ownCmeRow(members, currentUserId);
    if (member?.IsStaffer) {
      source = readOnlySource(
        CME_TITLE,
        CME_SUBTITLE,
        "CME weekly hours are read-only for your current dashboard view."
      );
    } else {
      // The web modal renders two editable grids: the Capacity Tracker
      // (VacationTaskType/VacationTemplateId pair) above the Weekly Deal Tracker.
      const sections: DynamicHoursSection[] = [];
      if (member && member.VacationTaskType && member.VacationTemplateId) {
        sections.push({
          key: "capacity-tracker",
          heading: "Capacity tracker",
          request: taskCommentRequest(member.VacationTaskType, member.VacationTemplateId),
          rows: [],
        });
      }
      if (member && member.DealTaskType && member.DealTemplateId) {
        sections.push({
          key: "deal-tracker",
          heading: "Weekly deal tracker",
          request: taskCommentRequest(member.DealTaskType, member.DealTemplateId),
          rows: [],
        });
      }
      source = {
        title: CME_TITLE,
        subtitle: CME_SUBTITLE,
        readOnlyMessage: null,
        isRealEstate: false,
        excludedLabels: [],
        sections,
      };
    }
  } else if (kind === "consumer-retail") {
    source = tupleSource(
      await HoursApi.getConsumerRetailHoursSource(),
      "Consumer & Retail",
      "Update upcoming-week, weekend, last-week hours, and your note to the staffer."
    );
  } else if (kind === "gpc") {
    source = tupleSource(
      await HoursApi.getGpcHoursSource(),
      "GPC",
      "Update upcoming-week, weekend, and last-week hours for your eligible deals."
    );
  } else {
    const reTitle = "Real Estate worklog";
    const reSubtitle = "Update daily hours, weekend plans, next-week estimates, and comments.";
    const realEstate = await HoursApi.getRealEstateHoursSource();
    if (realEstate.IsStaffer) {
      return readOnlySource(reTitle, reSubtitle, "Real Estate worklogs are read-only for staffer dashboard views.");
    }
    const member = ownRealEstateRow(realEstate, currentUserId);
    const trackers = member?.WorklogTracker ?? [];
    const first = trackers[0];
    source = {
      title: reTitle,
      subtitle: reSubtitle,
      readOnlyMessage: null,
      isRealEstate: true,
      excludedLabels: [],
      sections:
        first?.TaskTypeId && first.ProjectTypeId
          ? [
              {
                key: "worklog",
                heading: null,
                request: taskCommentRequest(
                  first.TaskTypeId,
                  first.ProjectTypeId,
                  trackers.map((tracker) => tracker.TaskId)
                ),
                rows: [],
              },
            ]
          : [],
    };
  }

  if (source.readOnlyMessage) return source;
  const sections = await Promise.all(
    source.sections.map(async (section) => {
      if (section.request.taskTypeId <= 0 || section.request.projectTypeId <= 0) return section;
      const rows = source.isRealEstate
        ? await HoursApi.getRealEstateDynamicHours(section.request)
        : await HoursApi.getDynamicHours(section.request);
      return { ...section, rows: Array.isArray(rows) ? rows : [] };
    })
  );
  return { ...source, sections };
}

function normalized(value: string | null | undefined): string {
  return (value ?? "").trim().toLocaleLowerCase();
}

function isTableField(field: DynamicHoursField): boolean {
  return normalized(field.FieldType) === "table";
}

function shownFields(row: DynamicHoursRow, source: DynamicHoursSource): DynamicHoursField[] {
  const excluded = new Set(source.excludedLabels.map(normalized));
  return row.DynamicFields.filter(
    (field) => field.Visible !== false && !isTableField(field) && !excluded.has(normalized(field.Label))
  );
}

function isHourField(field: DynamicHoursField): boolean {
  return normalized(field.Label).includes("hour");
}

// Mirrors Triyo.Web's isMaskValid: the value must match the admin-configured mask
// position by position ('#' means digit, anything else is a literal).
function matchesMask(value: string, mask: string): boolean {
  if (value.length !== mask.length) return false;
  for (let index = 0; index < mask.length; index += 1) {
    const maskChar = mask[index];
    const valueChar = value[index];
    if (maskChar === "#") {
      if (valueChar < "0" || valueChar > "9") return false;
    } else if (maskChar !== valueChar) {
      return false;
    }
  }
  return true;
}

function validateField(field: DynamicHoursField): string | null {
  const value = String(field.Data ?? "").trim();
  if (field.Required && !value) return "This field is required.";

  if (field.FieldMask) {
    if (!matchesMask(value, field.FieldMask)) return `Must follow the format: ${field.FieldMask}`;
  } else if (value && isHourField(field)) {
    if (value.includes(":")) {
      if (!/^\d+:[0-5]\d$/.test(value)) return "Use H:MM (for example, 7:30).";
    } else {
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed < 0) return "Enter hours greater than or equal to zero.";
    }
  }

  if (value && field.FieldRegex) {
    try {
      if (!new RegExp(field.FieldRegex).test(value)) {
        return field.FieldRegexDescription || "The value does not match the required format.";
      }
    } catch {
      // Some server-side .NET regex constructs are not valid JavaScript. The API remains authoritative.
    }
  }
  return null;
}

function rowChanged(row: DynamicHoursRow, original: DynamicHoursRow, source: DynamicHoursSource): boolean {
  const currentFields = shownFields(row, source);
  const originalById = new Map(original.DynamicFields.map((field) => [field.Id, String(field.Data ?? "")]));
  if (currentFields.some((field) => String(field.Data ?? "") !== (originalById.get(field.Id) ?? ""))) return true;
  return source.isRealEstate && String(row.Note ?? "") !== String(original.Note ?? "");
}

// The RE dashboard reads yes/no answers by comparing the stored text to "yes";
// literal "true"/"false" strings would never surface anywhere.
function yesNoValue(data: string): string {
  const value = data.trim().toLocaleLowerCase();
  if (value === "yes" || value === "true") return "Yes";
  if (value === "no" || value === "false") return "No";
  return "";
}

function controlForField(
  field: DynamicHoursField,
  projectName: string,
  onChange: (value: string) => void
): JSX.Element {
  const type = normalized(field.FieldType);
  const values = field.LookupValues?.filter(Boolean) ?? [];
  const common = {
    value: String(field.Data ?? ""),
    disabled: !!field.IsDisabled,
    "aria-label": `${field.Label} for ${projectName}`,
  };

  if (field.Lookup || values.length > 0 || type.includes("dropdown") || type.includes("select")) {
    return (
      <select {...common} onChange={(event) => onChange(event.target.value)}>
        <option value="">Select</option>
        {values.map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </select>
    );
  }

  if (type.includes("textarea") || type.includes("multi")) {
    return <textarea {...common} rows={2} onChange={(event) => onChange(event.target.value)} />;
  }

  if (type.includes("bool") || type.includes("check")) {
    return (
      <select {...common} value={yesNoValue(String(field.Data ?? ""))} onChange={(event) => onChange(event.target.value)}>
        <option value="">Select</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    );
  }

  return (
    <input
      {...common}
      type={type.includes("date") ? "date" : "text"}
      inputMode={isHourField(field) ? "decimal" : undefined}
      placeholder={field.Placeholder || undefined}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

export function DynamicHoursForm({ kind, currentUserId }: DynamicHoursFormProps): JSX.Element {
  const [source, setSource] = useState<DynamicHoursSource | null>(null);
  const [sections, setSections] = useState<DynamicHoursSection[]>([]);
  const [originalSections, setOriginalSections] = useState<DynamicHoursSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const nextSource = await loadSource(kind, currentUserId);
      if (requestId !== requestIdRef.current) return;
      setSource(nextSource);
      setSections(structuredClone(nextSource.sections));
      setOriginalSections(structuredClone(nextSource.sections));
    } catch (reason: unknown) {
      if (requestId === requestIdRef.current) {
        setSource(null);
        setSections([]);
        setOriginalSections([]);
        setError(reason instanceof Error ? reason.message : "Could not load this weekly-hours form.");
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [currentUserId, kind]);

  useEffect(() => {
    void load();
    return () => {
      requestIdRef.current += 1;
    };
  }, [load]);

  const originalByKey = useMemo(
    () =>
      new Map(
        originalSections.map((section) => [section.key, new Map(section.rows.map((row) => [row.Id, row]))] as const)
      ),
    [originalSections]
  );

  const changedRowsBySection = useMemo(
    () =>
      source
        ? sections.map((section) => ({
            section,
            changed: section.rows.filter((row) => {
              const original = originalByKey.get(section.key)?.get(row.Id);
              return original ? rowChanged(row, original, source) : false;
            }),
          }))
        : [],
    [originalByKey, sections, source]
  );
  const changedRowCount = changedRowsBySection.reduce((sum, entry) => sum + entry.changed.length, 0);

  // Match Triyo.Web: only fields the user touched can block saving. Server-delivered
  // rows with blank required fields must not lock the whole form.
  const touchedValidationErrors = useMemo(
    () =>
      source
        ? sections.flatMap((section) =>
            section.rows.flatMap((row) =>
              shownFields(row, source)
                .filter((field) => field.touched)
                .map(validateField)
                .filter(Boolean)
            )
          )
        : [],
    [sections, source]
  );
  const canSubmit = changedRowCount > 0 && touchedValidationErrors.length === 0 && !saving;
  const totalRows = sections.reduce((sum, section) => sum + section.rows.length, 0);

  const updateField = (sectionKey: string, rowId: number, fieldId: number, value: string) => {
    setSections((current) =>
      current.map((section) =>
        section.key === sectionKey
          ? {
              ...section,
              rows: section.rows.map((row) =>
                row.Id === rowId
                  ? {
                      ...row,
                      DynamicFields: row.DynamicFields.map((field) =>
                        field.Id === fieldId ? { ...field, Data: value, touched: true } : field
                      ),
                    }
                  : row
              ),
            }
          : section
      )
    );
    setSuccess(null);
  };

  const updateNote = (sectionKey: string, rowId: number, value: string) => {
    setSections((current) =>
      current.map((section) =>
        section.key === sectionKey
          ? { ...section, rows: section.rows.map((row) => (row.Id === rowId ? { ...row, Note: value } : row)) }
          : section
      )
    );
    setSuccess(null);
  };

  const submit = async () => {
    if (!canSubmit || !source) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const excluded = new Set(source.excludedLabels.map(normalized));
      const payload: DynamicHoursSaveRow[] = changedRowsBySection.flatMap(({ changed }) =>
        changed.map((row) => ({
          Id: row.Id,
          DynamicFields: row.DynamicFields.filter((field) => !excluded.has(normalized(field.Label))),
          ...(source.isRealEstate ? { Note: row.Note ?? "" } : {}),
        }))
      );
      await HoursApi.saveDynamicHours(payload);
      if (!source.isRealEstate) {
        try {
          await HoursApi.carryOverDashboardData();
        } catch {
          // The dashboard save already succeeded; carry-over is best-effort, matching the Web follow-up behavior.
        }
      }
      const refreshed = await loadSource(kind, currentUserId);
      setSource(refreshed);
      setSections(structuredClone(refreshed.sections));
      setOriginalSections(structuredClone(refreshed.sections));
      setSuccess("Weekly hours saved to TRIYO.");
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Could not save weekly hours.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="weekly-hours__state" role="status">
        <FluentSpinner label="Loading weekly hours…" />
      </div>
    );
  }

  if (!source) {
    return (
      <div className="weekly-hours__state" role="alert">
        <p>{error || "This weekly-hours form is not available."}</p>
        <button type="button" className="weekly-hours__secondary-button" onClick={() => void load()}>
          Try again
        </button>
      </div>
    );
  }

  if (source.readOnlyMessage) {
    // An expected, permanent configuration outcome — not a retryable failure.
    return (
      <div className="weekly-hours__form">
        <div className="weekly-hours__intro">
          <h3>{source.title}</h3>
          <p>{source.subtitle}</p>
        </div>
        <div className="weekly-hours__empty" role="status">
          {source.readOnlyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="weekly-hours__form">
      <div className="weekly-hours__intro">
        <h3>{source.title}</h3>
        <p>{source.subtitle}</p>
      </div>

      {totalRows === 0 ? (
        <div className="weekly-hours__empty">You do not have any active, eligible tasks for this form.</div>
      ) : (
        sections.map((section) =>
          section.rows.length === 0 ? null : (
            <div key={section.key} className="weekly-hours__rows">
              {section.heading ? <h4 className="weekly-hours__section-heading">{section.heading}</h4> : null}
              {section.rows.map((row) => {
                const projectName = row.Project?.Name || row.Task?.Name || `Task ${row.Id}`;
                const fields = shownFields(row, source);
                return (
                  <section key={row.Id} className="weekly-hours__deal-card">
                    <div className="weekly-hours__deal-heading">
                      <div>
                        <h4>{projectName}</h4>
                        <p>{row.LastUpdatedString ? `Last updated ${row.LastUpdatedString}` : "Weekly time log"}</p>
                      </div>
                    </div>

                    <div className="weekly-hours__dynamic-grid">
                      {fields.map((field) => {
                        const validation = field.touched ? validateField(field) : null;
                        return (
                          <label key={field.Id} className="weekly-hours__field weekly-hours__field--dynamic">
                            <span>{field.LabelShort || field.Label}</span>
                            {controlForField(field, projectName, (value) =>
                              updateField(section.key, row.Id, field.Id, value)
                            )}
                            {validation ? <small className="weekly-hours__validation">{validation}</small> : null}
                          </label>
                        );
                      })}
                    </div>

                    {source.isRealEstate ? (
                      <label className="weekly-hours__field weekly-hours__field--note">
                        <span>Comments</span>
                        <textarea
                          rows={2}
                          value={row.Note ?? ""}
                          aria-label={`Comments for ${projectName}`}
                          onChange={(event) => updateNote(section.key, row.Id, event.target.value)}
                        />
                      </label>
                    ) : null}
                  </section>
                );
              })}
            </div>
          )
        )
      )}

      {error ? (
        <div className="weekly-hours__message weekly-hours__message--error" role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="weekly-hours__message weekly-hours__message--success" role="status">
          <CheckmarkCircleRegular />
          {success}
        </div>
      ) : null}

      {totalRows > 0 ? (
        <div className="weekly-hours__actions">
          <button
            type="button"
            className="weekly-hours__secondary-button"
            disabled={saving}
            onClick={() => void load()}
          >
            Reset
          </button>
          <button
            type="button"
            className="weekly-hours__primary-button"
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            {saving ? "Saving…" : "Submit changes"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
